import { DndContext, DragOverlay } from "@dnd-kit/core";
import { useNavigate } from "react-router-dom";
import { AddableCourse, CourseLite, CourseSummary } from "../../types/course.response";
import { Requirement, CourseSet, Select } from "../../types/requirement.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { useCourseSetDnd } from "../../hooks/useCourseSetDnd";
import {
    isRequirementFulfilled,
    getCompletedUnitsCount,
    getRequiredUnitsCount,
    getUnitsLabel,
    getOptionProgress,
    hasUnitsRequirement,
    isOptionLikeRequirement,
} from "../../utils/requirementResponseUtils";
import CircularCheckmark from "../icons/CircularCheckmark";
import ChevronIcon from "../icons/ChevronIcon";
import RemoveDropZone from "../shared/RemoveDropZone";
import ChecklistCourseSetSlots from "./ChecklistCourseSetSlots";
import ChecklistGroupSlots from "./ChecklistGroupSlots";
import ChecklistOptionSection from "./ChecklistOptionSection";
import ChecklistOptionGroupSection from "./ChecklistOptionGroupSection";

interface ChecklistSectionProps {
    requirement: Requirement;
    coursesMap: Record<string, CourseSummary>;
    handleAddCourse: (course: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void | Promise<void>;
    handleDeleteCourse: (course: CourseLite) => void | Promise<void>;
    handleOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
}

function ProgressLabel({ requirement }: { requirement: Requirement }) {
    const completedCount = getCompletedUnitsCount(requirement);
    const requiredCount = getRequiredUnitsCount(requirement);
    const unitsLabel = getUnitsLabel(requirement);

    if (isOptionLikeRequirement(requirement)) {
        const { fulfilled, required } = getOptionProgress(requirement);
        const parts = [`${fulfilled} / ${required} options satisfied`];
        if (hasUnitsRequirement(requirement)) {
            parts.push(`${completedCount} / ${requiredCount} ${unitsLabel}`);
        }
        return (
            <span className={`text-xs md:text-sm font-medium text-gray-500`}>
                {parts.join(" · ")}
            </span>
        );
    }

    return (
        <span className={`text-xs md:text-sm font-medium text-gray-500`}>
            {completedCount} / {requiredCount} {unitsLabel}
        </span>
    );
}

export default function ChecklistSection({
    requirement,
    coursesMap,
    handleAddCourse,
    handleDeleteCourse,
    handleOverride,
}: ChecklistSectionProps) {
    const navigate = useNavigate();
    const { activeDrag, handleDragStart, handleDragEnd, handleDragCancel } = useCourseSetDnd(requirement, handleOverride);
    const isFulfilled = isRequirementFulfilled(requirement);

    // Adapter: slots pass (course, nodeId), we translate to Program's handleAddCourse signature
    const handleSlotAdd = async (course: AddableCourse, nodeId: string | null) => {
        if (nodeId && course.userItemId) {
            await handleOverride(course, {
                requirementId: requirement.info.id,
                nodeId,
                overrideType: "FORCE_APPLY",
            });
            return;
        }
        await handleAddCourse(
            course,
            nodeId ? [{ requirementId: requirement.info.id, nodeId, overrideType: "FORCE_APPLY" }] : [],
            null,
        );
    };

    const renderSlots = () => {
        switch (requirement.info.uiType) {
            case "LIST": {
                const courseSet = requirement.rootNode as CourseSet;
                return (
                    <div className="flex flex-col gap-3">
                        <ChecklistCourseSetSlots
                            courseSet={courseSet}
                            coursesMap={coursesMap}
                            requirementId={requirement.info.id}
                            onAdd={(course) => handleSlotAdd(course, courseSet.id)}
                            onAddExtra={(course) => handleSlotAdd(course, null)}
                            onDelete={handleDeleteCourse}
                            idPrefix="checklist-list"
                        />
                    </div>
                );
            }
            case "GROUP":
                return (
                    <ChecklistGroupSlots
                        rootNode={requirement.rootNode as Select}
                        requirement={requirement}
                        coursesMap={coursesMap}
                        onAdd={handleSlotAdd}
                        onDelete={handleDeleteCourse}
                    />
                );
            case "OPTION":
                return (
                    <ChecklistOptionSection
                        rootNode={requirement.rootNode as Select}
                        requirement={requirement}
                        coursesMap={coursesMap}
                        onAdd={handleSlotAdd}
                        onDelete={handleDeleteCourse}
                    />
                );
            case "OPTION_GROUP":
                return (
                    <ChecklistOptionGroupSection
                        rootNode={requirement.rootNode as Select}
                        requirement={requirement}
                        coursesMap={coursesMap}
                        onAdd={handleSlotAdd}
                        onDelete={handleDeleteCourse}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            <div className="transition-all pb-12 md:pb-16">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-y-1 mb-3">
                    <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => navigate(`/programs/${requirement.info.programId}/requirements/${requirement.info.id}`)}
                    >
                        <h2 className="text-base md:text-lg font-semibold text-gray-900">{requirement.info.name}</h2>
                        {isFulfilled && (
                            <div className="flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 bg-green-50 border border-green-100 rounded-full">
                                <CircularCheckmark className="h-3 w-3 md:h-3.5 md:w-3.5 text-green-500" />
                                <span className="text-[9px] md:text-[10px] font-bold text-green-600 uppercase tracking-wide md:tracking-wider">
                                    Satisfied
                                </span>
                            </div>
                        )}
                        <ChevronIcon direction="right" className="w-5 h-5" />
                    </div>
                    <ProgressLabel requirement={requirement} />
                </div>

                {/* Slots */}
                {renderSlots()}

                {/* Per-section remove zone */}
                <div className="mt-2">
                    <RemoveDropZone
                        id={`checklist-remove-${requirement.info.id}`}
                        isVisible={activeDrag?.isApplied === true}
                        layout="vertical"
                    />
                </div>
            </div>

            <DragOverlay>
                {activeDrag ? (
                    <div className="flex items-center gap-2.5 px-3 py-2 rounded-md border border-blue-300 bg-white shadow-lg cursor-grabbing">
                        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-400" />
                        <span className="font-mono text-sm text-gray-700">
                            {activeDrag.course.courseInfo.subject} {activeDrag.course.courseInfo.number}
                        </span>
                        <span className="text-sm text-gray-500 truncate max-w-[200px]">
                            {activeDrag.course.courseInfo.titleLong}
                        </span>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
